import { useEffect, useMemo, useRef, useState } from "react";
import { agentShortName, type Agent, type Session } from "@baton/contracts";
import { presenceFor, selectThread, summarizeRecipientStates, type ThreadMessage } from "./chat/thread.js";
import { buildMessageContent, emptyHandoffDraft, type HandoffDraftState } from "./compose.js";
import type { FlowViewer } from "./flow/flow-canvas.js";
import type { AgentRef } from "./flow/nodes.js";
import { HandoffFields } from "./handoff-fields.js";
import { HandoverBlock } from "./handover-block.js";
import type { SendDraft } from "./hooks/use-project-messages.js";
import type { DecryptedProjectGraph } from "./projects-view.js";

export type ChatTarget =
  | { kind: "agent"; agentId: string }
  | { kind: "wire"; wireId: string; sourceAgentId: string; targetAgentId: string };

/**
 * A conversation between two agents inside the selected project. In agent mode the viewer's
 * general agent talks to the peer; on a wire the viewer observes both ends and may address
 * either. Plaintext exists only in memory and in this drawer's DOM.
 */
export function AgentChatDrawer(props: {
  target: ChatTarget;
  graph: DecryptedProjectGraph;
  viewer: FlowViewer;
  agents: Agent[];
  sessions: Session[];
  messages: ThreadMessage[];
  sessionReady: boolean;
  error: string | null;
  onSend(draft: SendDraft): Promise<void>;
  onClose(): void;
  onConnect(agent: AgentRef): void;
}) {
  const me = props.viewer.generalAgentId;
  const [recipientId, setRecipientId] = useState<string>(props.target.kind === "agent" ? props.target.agentId : props.target.targetAgentId);
  const [type, setType] = useState<"chat" | "handoff">("chat");
  const [body, setBody] = useState("");
  const [handoff, setHandoff] = useState<HandoffDraftState>(() => emptyHandoffDraft());
  const [repoId, setRepoId] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setRecipientId(props.target.kind === "agent" ? props.target.agentId : props.target.targetAgentId);
    setHandoff(emptyHandoffDraft());
    setRepoId("");
    setSendError(null);
  }, [props.target]);
  // A different peer has different repositories; never carry the previous routing over.
  useEffect(() => { setRepoId(""); }, [recipientId]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, []);

  const [endpointA, endpointB] = props.target.kind === "agent"
    ? [me, props.target.agentId]
    : [props.target.sourceAgentId, props.target.targetAgentId];
  const thread = useMemo(
    () => endpointA && endpointB ? selectThread(props.messages, props.graph.project.id, endpointA, endpointB) : [],
    [props.messages, props.graph.project.id, endpointA, endpointB],
  );
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [thread.length]);

  const label = (agentId: string | null) => {
    const agent = props.agents.find((candidate) => candidate.id === agentId)
      ?? props.graph.agentAssignments.find((assignment) => assignment.agent.id === agentId)?.agent;
    if (!agent) return agentId?.slice(0, 8) ?? "unknown";
    const owner = ("ownerHandle" in agent ? agent.ownerHandle : undefined)
      ?? props.graph.members.find((member) => member.userId === agent.ownerUserId)?.handle;
    return agentShortName(agent, owner);
  };
  const peerId = props.target.kind === "agent" ? props.target.agentId : recipientId;
  const presence = presenceFor(peerId, props.sessions, now);
  const peerAgent = props.agents.find((agent) => agent.id === peerId) ?? null;
  const peerRepositories = props.graph.agentAssignments
    .filter((assignment) => assignment.agent.id === peerId && assignment.repositoryId)
    .map((assignment) => props.graph.repositories.find((repository) => repository.id === assignment.repositoryId))
    .filter((repository): repository is NonNullable<typeof repository> => Boolean(repository));
  const selectedRepository = peerRepositories.find((repository) => repository.id === repoId) ?? null;
  const lastFromPeer = [...thread].reverse().find((message) => message.senderAgentId === peerId) ?? null;
  const talkingToSelf = peerId === me;

  // The routing select is the one place a repository is picked here; the handover follows it.
  function pickRepository(nextId: string) {
    setRepoId(nextId);
    const repository = peerRepositories.find((candidate) => candidate.id === nextId) ?? null;
    setHandoff((current) => ({ ...current, repositoryId: repository?.id ?? null, branch: repository?.defaultBranch ?? "" }));
  }

  async function send(event?: React.FormEvent) {
    event?.preventDefault();
    if (!body.trim() || sending || talkingToSelf) return;
    setSending(true); setSendError(null);
    try {
      const content = buildMessageContent({
        type,
        body,
        handoff: { ...handoff, repositoryId: selectedRepository?.id ?? null },
        project: { id: props.graph.project.id, name: props.graph.project.name },
        repository: type === "handoff" ? selectedRepository : null,
      });
      await props.onSend({
        recipientAgentIds: [peerId],
        type,
        content,
        replyToMessageId: lastFromPeer?.id ?? null,
        repoId: selectedRepository?.id ?? null,
      });
      setBody("");
      setHandoff({ ...emptyHandoffDraft(), repositoryId: selectedRepository?.id ?? null, branch: selectedRepository?.defaultBranch ?? "" });
    } catch (caught) {
      setSendError(caught instanceof Error ? caught.message : "Could not send");
    } finally {
      setSending(false);
    }
  }

  const bodyField = <textarea
    value={body}
    onChange={(event) => setBody(event.target.value)}
    placeholder={talkingToSelf ? "Pick another agent to chat with." : type === "handoff" ? `Brief for ${label(peerId)}…` : `Message ${label(peerId)}…`}
    rows={3}
    aria-label="Message body"
    disabled={talkingToSelf}
    onKeyDown={(event) => { if ((event.ctrlKey || event.metaKey) && event.key === "Enter") void send(); }}
  />;

  return <aside className="chat-drawer" data-testid="agent-chat" aria-label={`Conversation with ${label(peerId)}`}>
    <header className="chat-header">
      <div>
        <p className="eyebrow">{props.target.kind === "wire" ? "Conversation wire" : "Chat"}</p>
        <h3>
          {props.target.kind === "wire"
            ? <>{label(props.target.sourceAgentId)} <span aria-hidden="true">⟷</span> {label(props.target.targetAgentId)}</>
            : label(peerId)}
        </h3>
        <p className="chat-presence">
          <span className={`presence-dot${presence.online ? " online" : ""}`} aria-hidden="true" />
          {presence.online ? `online via ${presence.client}` : "offline · delivered when its client listens"}
          {peerAgent && peerAgent.ownerUserId === props.viewer.userId && <button type="button" className="link" onClick={() => props.onConnect({ agentId: peerAgent.id, handle: peerAgent.handle })}>Connect</button>}
        </p>
      </div>
      <button type="button" className="modal-close" aria-label="Close chat" onClick={props.onClose}>×</button>
    </header>
    {props.error && <p className="banner error">{props.error}</p>}
    <div className="chat-messages" ref={listRef}>
      {thread.length === 0 && <p className="empty compact">{talkingToSelf ? "This is you: the dashboard speaks as this agent." : "No messages yet in this project between these agents."}</p>}
      {thread.map((message) => {
        const mine = message.senderAgentId === me;
        return <article key={message.id} className={`chat-bubble ${mine ? "mine" : "theirs"}${message.pending ? " pending" : ""}${message.failed ? " failed" : ""}`}>
          <header>
            <strong>{label(message.senderAgentId)}</strong>
            <span className={`type ${message.type}`}>{message.type}</span>
            <time dateTime={message.createdAt}>{new Intl.DateTimeFormat(undefined, { timeStyle: "short", dateStyle: "short" }).format(new Date(message.createdAt))}</time>
          </header>
          {message.content.subject && <p className="chat-subject">{message.content.subject}</p>}
          <p className="chat-body">{message.content.body}</p>
          {message.content.acceptanceCriteria && message.content.acceptanceCriteria.length > 0 && <ul className="chat-criteria">
            {message.content.acceptanceCriteria.map((item) => <li key={item}>{item}</li>)}
          </ul>}
          {message.content.handover && <HandoverBlock document={message.content.handover} />}
          <footer className="chat-states">
            {summarizeRecipientStates(message).map((state) => <span key={state.agentId} className={`chat-state-chip ${state.outcome ?? state.state}`}>
              {label(state.agentId)} · {state.outcome ?? state.state}
            </span>)}
            {message.pending && <span className="chat-state-chip pending">sending…</span>}
            {message.failed && <span className="chat-state-chip failed">failed</span>}
          </footer>
          {message.recipientStates.some((state) => state.result) && <p className="chat-result muted">
            {message.recipientStates.map((state) => state.result?.summary).filter(Boolean).join(" · ")}
          </p>}
        </article>;
      })}
    </div>
    <form className="chat-composer" onSubmit={send}>
      {props.target.kind === "wire" && <label className="chat-recipient">To
        <select value={recipientId} onChange={(event) => setRecipientId(event.target.value)}>
          <option value={props.target.targetAgentId}>{label(props.target.targetAgentId)}</option>
          <option value={props.target.sourceAgentId}>{label(props.target.sourceAgentId)}</option>
        </select>
      </label>}
      <div className="chat-composer-row">
        <div className="tabs" role="tablist" aria-label="Message type">
          <button type="button" role="tab" aria-selected={type === "chat"} className={type === "chat" ? "active" : ""} onClick={() => setType("chat")}>Chat</button>
          <button type="button" role="tab" aria-selected={type === "handoff"} className={type === "handoff" ? "active" : ""} onClick={() => setType("handoff")}>Handoff</button>
        </div>
        {peerRepositories.length > 0 && <select value={repoId} onChange={(event) => pickRepository(event.target.value)} aria-label="Repository">
          <option value="">No repository</option>
          {peerRepositories.map((repository) => <option key={repository.id} value={repository.id}>{repository.ownerHandle}/{repository.name}</option>)}
        </select>}
      </div>
      {type === "handoff"
        ? <HandoffFields compact value={{ ...handoff, repositoryId: selectedRepository?.id ?? null }} onChange={setHandoff} repositories={peerRepositories} showRepositoryPicker={false}>{bodyField}</HandoffFields>
        : bodyField}
      {sendError && <p className="error">{sendError}</p>}
      <div className="chat-composer-actions">
        <span className="muted compact">{props.sessionReady ? "Sent as your general agent, encrypted in this page." : "Preparing your dashboard session…"}</span>
        <button type="submit" className="primary" disabled={sending || talkingToSelf || !body.trim() || !props.sessionReady}>{sending ? "Sending…" : "Send"}</button>
      </div>
    </form>
  </aside>;
}
